import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Copy, Rocket, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "mt-1 w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]";
const lab = "text-[10px] uppercase tracking-widest text-white/40 font-semibold";

export default function PromoEditor({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [cfg, setCfg] = useState(null);
  const [presets, setPresets] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/weekly-special/config`, auth),
        axios.get(`${API}/weekly-special/presets`, auth),
      ]);
      setCfg(c.data); setPresets(p.data);
    } catch { toast.error("Could not load promotion settings."); }
    axios.get(`${API}/weekly-special/pipelines`, auth).then((r) => setPipelines(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        promo_name: cfg.promo_name || "", promo_price: Number(cfg.promo_price) || 0,
        promo_deposit: Number(cfg.promo_deposit) || 0, promo_description: cfg.promo_description || "",
        promo_terms: cfg.promo_terms || "", promo_start_date: cfg.promo_start_date || "",
        promo_end_date: cfg.promo_end_date || "", expires_at: cfg.expires_at || "",
        max_bookings: Number(cfg.max_bookings) || 0, bookings_taken: Number(cfg.bookings_taken) || 0,
        promo_image: cfg.promo_image || "", active: cfg.active !== false,
        ghl_pipeline_id: cfg.ghl_pipeline_id || "", ghl_stage_id: cfg.ghl_stage_id || "",
      };
      await axios.patch(`${API}/weekly-special/config`, body, auth);
      toast.success("Promotion saved — landing page updated instantly.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const savePreset = async () => {
    const name = window.prompt("Name this promotion preset (e.g. $222 Weekend Special):", cfg?.promo_name || "");
    if (!name?.trim()) return;
    await axios.post(`${API}/weekly-special/presets`, { name }, auth);
    toast.success("Preset saved — duplicate it any time.");
    load();
  };
  const launch = async (p) => {
    if (!window.confirm(`Launch "${p.name}" now? It replaces the current promotion settings and resets the bookings counter.`)) return;
    await axios.post(`${API}/weekly-special/presets/${p.id}/launch`, {}, auth);
    toast.success(`"${p.name}" is LIVE.`);
    load();
  };
  const delPreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    await axios.delete(`${API}/weekly-special/presets/${p.id}`, auth);
    load();
  };

  if (!cfg) return null;
  const stages = (pipelines.find((p) => p.id === cfg.ghl_pipeline_id)?.stages) || [];

  return (
    <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5 space-y-4" data-testid="promo-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Megaphone size={17} className="text-[#F2B705]" /> Promotion Editor</h3>
        <label className="flex items-center gap-2 text-xs font-bold">
          <input data-testid="promo-active" type="checkbox" checked={cfg.active !== false} onChange={(e) => set("active", e.target.checked)} className="accent-[#F2B705]" />
          <span className={cfg.active !== false ? "text-emerald-300" : "text-red-400"}>{cfg.active !== false ? "PROMOTION LIVE" : "PROMOTION OFF"}</span>
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className={lab}>Promotion name<input data-testid="promo-name" className={inp} value={cfg.promo_name || ""} onChange={(e) => set("promo_name", e.target.value)} placeholder="$222 Weekend Special" /></label>
        <label className={lab}>Promo price ($)<input data-testid="promo-price" className={inp} value={cfg.promo_price || ""} onChange={(e) => set("promo_price", e.target.value)} placeholder="222" /></label>
        <label className={lab}>Deposit ($)<input className={inp} value={cfg.promo_deposit || ""} onChange={(e) => set("promo_deposit", e.target.value)} placeholder="100" /></label>
        <label className={lab}>Rental start date<input type="date" className={inp} value={cfg.promo_start_date || ""} onChange={(e) => set("promo_start_date", e.target.value)} /></label>
        <label className={lab}>Rental end date<input type="date" className={inp} value={cfg.promo_end_date || ""} onChange={(e) => set("promo_end_date", e.target.value)} /></label>
        <label className={lab}>Expiration (auto-close)<input data-testid="promo-expires" type="date" className={inp} value={cfg.expires_at || ""} onChange={(e) => set("expires_at", e.target.value)} /></label>
        <label className={lab}>Booking limit (0 = unlimited)<input data-testid="promo-max" className={inp} value={cfg.max_bookings ?? ""} onChange={(e) => set("max_bookings", e.target.value)} /></label>
        <label className={lab}>Bookings taken<input data-testid="promo-taken" className={inp} value={cfg.bookings_taken ?? ""} onChange={(e) => set("bookings_taken", e.target.value)} /></label>
        <label className={lab}>Custom photo URL (optional)<input className={inp} value={cfg.promo_image || ""} onChange={(e) => set("promo_image", e.target.value)} placeholder="https://…" /></label>
      </div>
      <label className={lab}>Description<textarea className={inp} rows={2} value={cfg.promo_description || ""} onChange={(e) => set("promo_description", e.target.value)} placeholder="3-day weekend special — Friday to Monday, all-in." /></label>
      <label className={lab}>Terms &amp; conditions<textarea className={inp} rows={2} value={cfg.promo_terms || ""} onChange={(e) => set("promo_terms", e.target.value)} placeholder="21+, valid license & insurance required…" /></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={lab}>GoHighLevel pipeline
          <select data-testid="promo-pipeline" className={inp} value={cfg.ghl_pipeline_id || ""} onChange={(e) => set("ghl_pipeline_id", e.target.value)}>
            <option value="">— none —</option>
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className={lab}>Pipeline stage
          <select data-testid="promo-stage" className={inp} value={cfg.ghl_stage_id || ""} onChange={(e) => set("ghl_stage_id", e.target.value)}>
            <option value="">— none —</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="promo-save" onClick={save} disabled={busy} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-50"><Save size={14} /> Save Promotion</button>
        <button data-testid="promo-save-preset" onClick={savePreset} className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-white/20 text-white/70 text-sm font-bold hover:bg-white/10"><Copy size={14} /> Save as Preset</button>
      </div>
      {presets.length > 0 && (
        <div className="border-t border-white/10 pt-3">
          <p className={lab}>Past promotions — duplicate &amp; launch</p>
          <div className="mt-2 space-y-1.5">
            {presets.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm bg-[#0A0A0A] border border-white/10 rounded px-3 py-2">
                <span className="text-white/80 font-semibold">{p.name} <span className="text-white/30 text-xs">{String(p.created_at).slice(0, 10)}</span></span>
                <span className="flex gap-2">
                  <button data-testid={`preset-launch-${p.id}`} onClick={() => launch(p)} className="flex items-center gap-1 text-xs border border-emerald-500/60 text-emerald-300 px-2.5 py-1 rounded hover:bg-emerald-500 hover:text-black"><Rocket size={11} /> Launch</button>
                  <button onClick={() => delPreset(p)} className="text-xs border border-white/15 text-white/40 px-2 py-1 rounded hover:text-red-400"><Trash2 size={11} /></button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
